import { Button, Card, Descriptions, Typography, message } from 'antd';
import React, { memo } from 'react';
import dayjs from 'dayjs';
import {
    FaBirthdayCake,
    FaEnvelope,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaSchool, 
    FaUsers,
    FaBookReader, 
    FaUserCircle 
} from 'react-icons/fa';

const { Title } = Typography;

// Helper to create a title with an icon
const IconTitle = ({ icon, text, level, isMainSectionTitle = false }) => {
    const IconComponent = icon;
    let titleStyle = { display: 'flex', alignItems: 'center' };
    let iconStyle = { marginRight: '8px', verticalAlign: 'middle' };
    let iconSize = '1.1em'; 
    let textSize = 'inherit'; 

    if (isMainSectionTitle) {
        iconSize = '1.2em'; 
        textSize = '1.25rem'; 
        // Reduced marginBottom for main section titles
        titleStyle = { ...titleStyle, color: '#1890ff', marginBottom: '12px' }; 
    } else if (level === 5) { 
        iconSize = '1.2em';
        textSize = '1.1rem'; 
        // Reduced marginBottom for level 5 titles
        titleStyle = { ...titleStyle, marginBottom: '12px', color: '#555' }; 
    }


    if (level) { 
        return (
            <Title level={level} style={{...titleStyle, fontSize: textSize}}>
                <IconComponent style={iconStyle} size={iconSize} />
                {text}
            </Title>
        );
    }
    return (
        <span style={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <IconComponent style={iconStyle} size="1.1em" />
            {text}
        </span>
    );
};


/**
 * Memoized component for displaying applicant personal information, including contact details and family contacts.
 * @param {Object} props - Component props
 * @param {Object} props.data - Applicant data object
 */
const PersonalInfo = memo(({ data }) => {
    if (!data) return null;

    const formatAddress = (houseNo, barangay, city, province, postalCode) => {
        const parts = [houseNo, barangay, city, province, postalCode].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'N/A';
    };

    const permanentAddress = formatAddress(
        data.permanentHouseNo,
        data.permanentBarangay,
        data.permanentCity,
        data.permanentProvince,
        data.permanentPostalCode
    );

    const presentAddress = formatAddress(
        data.presentHouseNo,
        data.presentBarangay,
        data.presentCity,
        data.presentProvince,
        data.presentPostalCode
    );
    
    const formatDateOfBirth = (dob, birthDateString) => {
        if (dob) {
            return dayjs(dob).isValid() ? dayjs(dob).format('MMMM D, YYYY') : 'N/A';
        }
        if (birthDateString) {
            return dayjs(birthDateString).isValid() ? dayjs(birthDateString).format('MMMM D, YYYY') : 'N/A';
        }
        return 'N/A';
    };

    const formatFamilyContactAddress = (contact) => {
        const parts = [contact.houseNo, contact.city, contact.province, contact.country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'N/A';
    };

    const sectionSpacing = { marginBottom: 16 }; // Standard reduced space between sections
    const subSectionSpacing = { marginTop: 12, marginBottom: 12 }; // For nested sections like entry level

    return (
        <Card 
            className="mb-4" 
            title={ 
                <Title level={4} style={{ display: 'flex', alignItems: 'center', marginBottom: 0, fontSize: '1.35rem', color: '#1677ff' }}> 
                    <FaUserCircle style={{ marginRight: '10px', verticalAlign: 'middle' }} size="1.4em" />
                    Personal Information
                </Title>
            }
        >
            {/* Basic Information Section */}
            <Descriptions bordered column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} size="small" style={sectionSpacing}>
                <Descriptions.Item label="Prefix">{data.prefix || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="First Name">{data.firstName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Middle Name">{data.middleName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Last Name">{data.lastName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Suffix">{data.suffix || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Gender">{data.gender || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="LRN No.">{data.lrnNo || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Civil Status">{data.civilStatus || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Religion">{data.religion || 'N/A'}</Descriptions.Item>
            </Descriptions>

            {/* Birth Details Section */}
            <Descriptions 
                title={<IconTitle icon={FaBirthdayCake} text="Birth Details" />}
                bordered 
                column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} 
                size="small" style={sectionSpacing}
            >
                <Descriptions.Item label="Date of Birth">{formatDateOfBirth(data.dob, data.birthDate)}</Descriptions.Item>
                <Descriptions.Item label="Country of Birth">{data.countryOfBirth || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Place of Birth (City)">{data.birthPlaceCity || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Place of Birth (Province)">{data.birthPlaceProvince || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Nationality">{data.nationality || 'N/A'}</Descriptions.Item>
            </Descriptions>


            {/* Applicant's Contact Information Section */}
            <Descriptions 
                title={<IconTitle icon={FaEnvelope} text="Applicant's Contact Information" />}
                bordered 
                column={{ xxl: 2, xl: 2, lg: 1, md: 1, sm: 1, xs: 1 }} 
                size="small" style={sectionSpacing}
            >
                <Descriptions.Item label="Email">{data.email || data.emailAddress || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Mobile No.">{data.mobile || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Telephone No.">{data.telephoneNo || 'N/A'}</Descriptions.Item>
            </Descriptions>

            {/* Applicant's Address Information Section */}
            <Descriptions 
                title={<IconTitle icon={FaMapMarkerAlt} text="Applicant's Address Information" />}
                bordered 
                column={1} size="small" style={sectionSpacing}
            >
                <Descriptions.Item label="Permanent Address">{permanentAddress}</Descriptions.Item>
                <Descriptions.Item label="Present Address">{presentAddress}</Descriptions.Item>
            </Descriptions>
            
            {/* --- Previous Educational Background Section --- */}
            {(data.entryLevel || data.elementarySchoolName || data.juniorHighSchoolName) && (
                 <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 16 }}>
                    <IconTitle icon={FaGraduationCap} text="Previous Educational Background" level={5} isMainSectionTitle />
                    {data.entryLevel && (
                        <Descriptions bordered column={1} size="small" style={subSectionSpacing}
                            title={<IconTitle icon={FaBookReader} text="Entry Level Details" />}
                        >
                            <Descriptions.Item label="Entry Level">{data.entryLevel}</Descriptions.Item>
                        </Descriptions>
                    )}
                 </div>
            )}
            
            {data.elementarySchoolName && (
                <div style={{ 
                    // Adjusted marginTop: if entryLevel was NOT shown but this is, add a bit more top space, otherwise minimal
                    marginTop: data.entryLevel ? 0 : ((data.elementarySchoolName || data.juniorHighSchoolName) ? 12 : 0), 
                    marginBottom: 12 
                }}> 
                    <Descriptions 
                        title={<IconTitle icon={FaSchool} text="Elementary Education" />}
                        bordered 
                        column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} 
                        size="small"
                    >
                        <Descriptions.Item label="School Name">{data.elementarySchoolName}</Descriptions.Item>
                        <Descriptions.Item label="Last Year Attended">{data.elementaryLastYearAttended || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Remarks/Highest Achievement">{data.elementaryRemarks || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                </div>
            )}

            {data.juniorHighSchoolName && (
                <div style={{ 
                    marginTop: data.elementarySchoolName ? 0 : (data.entryLevel ? 0 : 12),
                    marginBottom: (data.familyContacts && data.familyContacts.length > 0) ? 12 : 0 
                }}>
                    <Descriptions 
                        title={<IconTitle icon={FaSchool} text="Junior High School Education" />}
                        bordered 
                        column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} 
                        size="small"
                    >
                        <Descriptions.Item label="School Name">{data.juniorHighSchoolName}</Descriptions.Item>
                        <Descriptions.Item label="Last Year Attended">{data.juniorHighLastYearAttended || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Remarks/Highest Achievement">{data.juniorHighRemarks || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                </div>
            )}
            {/* --- End of Previous Educational Background Section --- */}

            {data.familyContacts && data.familyContacts.length > 0 && (
                <div style={{ 
                    // Add top border and padding if it's the first section after others, or if no educational background was present
                    marginTop: (!data.elementarySchoolName && !data.juniorHighSchoolName && !data.entryLevel) ? 16 : 0, 
                    borderTop: '1px solid #f0f0f0', 
                    paddingTop: 16 
                }}> 
                    <IconTitle icon={FaUsers} text="Family Contacts" level={5} isMainSectionTitle />
                    {data.familyContacts.slice(0, 2).map((contact, index) => (
                        <Descriptions
                            key={contact._id?.$oid || index}
                            title={`${contact.relationship} - ${contact.firstName} ${contact.lastName}`}
                            bordered
                            column={{ xxl: 2, xl: 2, lg: 1, md: 1, sm: 1, xs: 1 }}
                            size="small"
                            style={{ marginBottom: 12 }} // Reduced space between individual contacts
                        >
                            <Descriptions.Item label="Full Name">{`${contact.firstName || ''} ${contact.middleName || ''} ${contact.lastName || ''}`.trim() || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Relationship">{contact.relationship || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Occupation">{contact.occupation || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Mobile No.">{contact.mobileNo || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Email Address">{contact.emailAddress || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Emergency Contact">{contact.isEmergencyContact ? 'Yes' : 'No'}</Descriptions.Item>
                            <Descriptions.Item label="Address" span={2}> 
                                {formatFamilyContactAddress(contact)}
                            </Descriptions.Item>
                        </Descriptions>
                    ))}
                    {data.familyContacts.length > 2 && (
                        <Button type="link" style={{paddingLeft: 0, marginTop: -4}} onClick={() => message.info(`There are ${data.familyContacts.length - 2} more contacts available. Full view needed for all.`)}>
                            View {data.familyContacts.length - 2} more contacts
                        </Button>
                    )}
                </div>
            )}
        </Card>
    );
});

export default PersonalInfo;